////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// encounter-summary-render.ts — TypeScript port of includes/js/encounter-summary-render.js
// ----------------------------------------------------------------------------------------------------
// Bundled into includes/js/dist/encounter-summary-bundle.js via
// tools/build/build-encounter-summary-bundle.mjs. Imported by ./index.ts which Object.assigns
// the exports onto window — getEncounterSettingSummary in template.js keeps calling
// window.renderEncounterSidelogSummary + window.renderCampaignTimeWidget unchanged.
//
// Two renderers:
//   • renderEncounterSidelogSummary — top-of-sidelog encounter context card.
//   • renderCampaignTimeWidget     — campaign calendar date/time card.
//
// Both return HTML strings consumed by getEncounterSettingSummary in template.js, which
// mounts them once; subsequent pushes patch their individual leaves.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import type { EncounterArr, CampaignArr } from '../shared/array-types';
import { withAsyncDeadline } from '../shared/async-deadline';
import { hasCampaignPermission, encounterField, computedValue } from '../shared/array-types';
import { effectiveEncounterLightId } from '../shared/env-types';
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import type { Ft3dCampaignHudState } from '../shared/ft3d-campaign-hud';

// Campaign reshape (2026-06-04): campaigns-table columns live in `campaignArr.campaigns` (bare
// scalars); genuinely-computed display values (calendar_current_*_f) live in `campaignArr.computed.campaign`.
// Bucket-first reads with a legacy `[0]`-wrapped fallback for un-rolled blobs.
// Catalog shell unwrap: {items:{item_id,...}} -> item_id (mirrors PHP campaign_field/layer_value),
// so shelled catalog columns (e.g. campaign_sun_state) still read as the UUID.
function _ftUnwrapShell(v: unknown): unknown {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
        const items = (v as { items?: { item_id?: unknown } }).items;
        if (items && typeof items.item_id === 'string') return items.item_id;
    }
    return v;
}
function _ftCamField(arr: CampaignArr | null | undefined, key: string): string | undefined {
    if (!arr) return undefined;
    const bucket = arr.campaigns;
    if (bucket && typeof bucket === 'object' && key in bucket) {
        const v = _ftUnwrapShell(bucket[key]);
        return v == null ? undefined : String(v);
    }
    const wrapped = (arr as { [k: string]: unknown })[key];
    if (Array.isArray(wrapped)) return wrapped[0] == null ? undefined : String(wrapped[0]);
    return wrapped == null ? undefined : String(wrapped);
}
function _ftCamComputed(arr: CampaignArr | null | undefined, key: string): string | undefined {
    const c = arr?.computed?.campaign;
    if (c && typeof c === 'object' && key in c) {
        const v = (c as { [k: string]: unknown })[key];
        return v == null ? undefined : String(v);
    }
    const wrapped = (arr as { [k: string]: unknown } | undefined)?.[key];
    if (Array.isArray(wrapped)) return wrapped[0] == null ? undefined : String(wrapped[0]);
    return wrapped == null ? undefined : String(wrapped);
}

function _ftHudText(value: unknown, fallback: string, maxLength: number): string {
    const normalized = String(value ?? '').replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
    return (normalized || fallback).slice(0, maxLength);
}

/**
 * Player-safe display projection for the embedded FT3D context dock. The parent remains the
 * encounter/campaign authority; this function deliberately returns no arrays, notes, permissions,
 * transport actions, roll data, audio state, or writable identifiers.
 */
export function projectFt3dCampaignHudState(
    encounterArr: EncounterArr | null | undefined,
    campaignArr: CampaignArr | null | undefined,
): Ft3dCampaignHudState | null {
    if (!encounterArr || !campaignArr) return null;
    const timeOfDay = _ftCamComputed(campaignArr, 'calendar_current_time_f');
    const compactDate = _ftCamComputed(campaignArr, 'calendar_current_date_f');
    const fullDate = _ftCamComputed(campaignArr, 'calendar_current_full_date_named_f');
    if (!timeOfDay || !compactDate || !fullDate) return null;

    const gmView = hasCampaignPermission(campaignArr);
    const inCombat = parseInt(String(encounterField(encounterArr, 'in_combat') ?? '0'), 10) === 1;
    const currentRoundRaw = encounterField(encounterArr, 'current_round');
    const currentRound = inCombat && currentRoundRaw !== undefined && currentRoundRaw !== null
        && String(currentRoundRaw).trim() !== ''
        ? _ftHudText(currentRoundRaw, '', 24)
        : null;
    const campaignPresent = _ftCamField(campaignArr, 'campaign_id') !== undefined;
    const havenDisabled = parseInt(
        _ftCamField(campaignArr, 'campaign_disable_player_haven_return') ?? '0',
        10,
    ) === 1;
    const zone = gmView ? computedValue<string>(encounterArr, 'encounter', 'encounter_zone') : null;
    const locationName = _ftHudText(
        encounterField(encounterArr, 'encounter_location_name')
            ?? encounterField(encounterArr, 'encounter_location_parent_name'),
        '',
        120,
    ) || null;

    return {
        schema: 'ft.ft3d.campaign-hud.v1',
        queryOnly: true,
        writes: 0,
        view: gmView ? 'gm' : 'player',
        encounterName: _ftHudText(
            encounterField(encounterArr, 'encounter_name'),
            'Unknown encounter',
            120,
        ),
        locationName,
        zoneName: zone ? _ftHudText(zone, '', 120) : null,
        status: {
            seasonIconId: _ftCamField(campaignArr, 'month_season') ?? null,
            sunIconId: _ftCamField(campaignArr, 'campaign_sun_state') ?? null,
            inCombat,
            currentRound,
            havenReturn: inCombat || !campaignPresent
                ? 'hidden'
                : (havenDisabled ? 'unavailable' : 'available'),
        },
        time: {
            timeOfDay: _ftHudText(timeOfDay, '—', 48),
            compactDate: _ftHudText(compactDate, '—', 80),
            fullDate: _ftHudText(fullDate, '—', 180),
        },
    };
}


/** Rooms in an encounter: its stored shape's boxes, or 1 for a legacy cuboid (mirrors encounter_room_count in ft_encounter_list_rows()). */
const summaryRoomCounts = new WeakMap<EncounterArr, { shape: unknown; count: number }>();
function encounterRoomCount(encounterArr: EncounterArr): number {
    const raw = encounterField<unknown>(encounterArr, 'encounter_shape');
    const held = summaryRoomCounts.get(encounterArr);
    if (held && held.shape === raw && typeof raw === 'string') return held.count;
    let shape = raw;
    if (typeof shape === 'string' && shape !== '') {
        try { shape = JSON.parse(shape); } catch { shape = null; }
    }
    const boxes = shape && typeof shape === 'object' ? (shape as { boxes?: unknown }).boxes : null;
    const count = Array.isArray(boxes) ? boxes.length : 1;
    summaryRoomCounts.set(encounterArr, { shape: raw, count });
    return count;
}

function summaryIconSlot(key: string, value: string, optional = false): string {
    return `<div data-ft-summary-icon="${key}" data-ft-summary-value="${value}" class="p-1${key === 'source' ? ' border rounded' : ''}"${optional && !value ? ' hidden' : ''}><span role="button" onclick="openItemInfo('${value}');">${optional && !value ? '' : iconMap(value)}</span></div>`;
}

function renderSummaryCombatStatus(encounterArr: EncounterArr, campaignArr: CampaignArr): { inCombatHiddenInput: string; rightClusterHtml: string } {
    // In combat (hidden + icon — icon rendered in right cluster below).
    // in_combat is elided from the encounters bucket when 0 (blank-field elision,
    // build_encounter_arr.php) — absence means out of combat, so default to '0'
    // rather than skipping the icon/hidden input entirely.
    const inCombat = encounterField(encounterArr, 'in_combat') ?? '0';
    const inCombatHiddenInput = `<input id="input_hidden_encounter_quick_summary_in_combat" type="hidden" value="${inCombat}">`;
    let roundBadgeHtml = '';
    const currentRound = encounterField(encounterArr, 'current_round');
    if (parseInt(String(inCombat), 10) === 1) {
        roundBadgeHtml = `<span id="span_encounter_quick_summary_round"${currentRound == null || currentRound === '' ? ' hidden' : ''} class="${currentRound == null || currentRound === '' ? 'd-none ' : ''}badge rounded-pill bg-success text-dark border border-dark fs-5 lh-1 d-inline-flex align-items-center justify-content-center fw-semibold py-1" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="Current Round">${String(currentRound ?? '')}</span>`;
    }
    // Mount the running mirror once. Scalar summary patches retain its live countdown nodes.
    let turnTimerBadgeHtml = '';
    if (parseInt(String(inCombat), 10) === 1) {
        const mirror = (window as { FateTide?: { turnTimerMirror?: { remaining?: number; running?: number; encounterId?: string } } })
            .FateTide?.turnTimerMirror;
        const encId = encounterField<string>(encounterArr, 'encounter_id') ?? '';
        const mirrorMatches = !!mirror
            && typeof mirror.remaining === 'number'
            && (!mirror.encounterId || mirror.encounterId === encId);
        // Always emit the badge in combat so the per-second updater (ftRenderTurnTimerMirrorBadge)
        // always has an element to poke. Until a value is known (player loaded mid-turn before the
        // GM's next action), show a neutral placeholder rather than a misleading default.
        const running = mirrorMatches && mirror!.running ? 1 : 0;
        const remainingTxt = mirrorMatches ? String(Math.max(0, mirror!.remaining as number)) : '–';
        const colorClass = running ? 'bg-warning' : 'bg-secondary';
        // Both state icons are rendered; the live updater (ftRenderTurnTimerMirrorBadge in
        // template.js) toggles their visibility + the badge color when run-state flips between
        // full summary re-renders, so it never needs iconMap.
        const runHidden = running ? '' : ' style="display:none;"';
        const pauseHidden = running ? ' style="display:none;"' : '';
        turnTimerBadgeHtml = `<span id="span_encounter_summary_turn_timer" class="badge rounded-pill ${colorClass} text-dark border border-dark fs-6 lh-1 d-inline-flex align-items-center justify-content-center gap-1 fw-semibold py-1"><span id="span_encounter_summary_turn_timer_icon_run"${runHidden}>${iconMap('turn_timer_resumed', 14, 14)}</span><span id="span_encounter_summary_turn_timer_icon_pause"${pauseHidden}>${iconMap('turn_timer_paused', 14, 14)}</span><span id="span_encounter_summary_turn_timer_value">${remainingTxt}</span></span>`;
    }

    const inCombatIconHtml = `
<div class="d-flex align-items-center gap-2">
    ${iconMap(parseInt(String(inCombat), 10) === 1 ? 'in_combat' : 'out_of_combat')}
    ${roundBadgeHtml}
    ${turnTimerBadgeHtml}
</div>`;

    // Haven return (campaign): mirrors turn-widget Row 2 — enabled when not disabled.
    // Hidden entirely IN COMBAT — you can't retreat to camp/stronghold mid-fight, so the
    // indicator is meaningless there (and dropping it frees the status row to fit one line;
    // the matching character-sheet buttons are gated the same way in movement.ts).
    let havenIconHtml = '';
    // The flag is elided from the campaigns bucket when 0 (haven enabled), so gate on the
    // campaign being present and default the disable flag to 0.
    if (parseInt(String(inCombat), 10) !== 1 && _ftCamField(campaignArr, 'campaign_id') !== undefined) {
        const havenEnabled = parseInt(_ftCamField(campaignArr, 'campaign_disable_player_haven_return') ?? '0', 10) !== 1;
        havenIconHtml = `${iconMap(havenEnabled ? 'haven_return_enabled' : 'haven_return_disabled')}`;
    }

    const rightClusterInner = `${havenIconHtml}${inCombatIconHtml}`;
    const rightClusterHtml = rightClusterInner
        ? `<div class="d-flex align-items-center gap-2 py-2 px-2">${rightClusterInner}</div>`
        : '';

    return { inCombatHiddenInput, rightClusterHtml };
}

/**
 * Renders the encounter sidelog summary card (encounter name + room count, location
 * parent + zone, terrain/season/weather/sun/light icon row, haven-return +
 * in-combat indicators with current-round badge).
 *
 * Zone label `(encounter_zone)` is gated on campaign_permission == 1 (GM-only),
 * matching the legacy PHP partial. The encounter_zone value is derived
 * server-side in build_encounter_arr.php (prefers per-encounter
 * encounter_zone_prefix over location_zone_prefix).
 */
export function renderEncounterSidelogSummary(encounterArr: EncounterArr, campaignArr: CampaignArr): string {
    // Basic validations (fallback to empty/safe values)
    const encounterName = encounterField<string>(encounterArr, 'encounter_name') ?? 'Unknown Encounter';
    const roomCount = encounterRoomCount(encounterArr);
    const locationParentName = encounterField<string>(encounterArr, 'encounter_location_parent_name') ?? '';

    // Zone (conditional on permission)
    let zoneHtml = '';
    const zone = computedValue<string>(encounterArr, 'encounter', 'encounter_zone');
    if (zone && hasCampaignPermission(campaignArr)) {
        zoneHtml = `(${zone})`;
    }

    // Icons (fallback to empty)
    const environment = encounterField<string>(encounterArr, 'encounter_environment') ?? '';
    const weather = encounterField<string>(encounterArr, 'encounter_weather') ?? '';
    const light = effectiveEncounterLightId(encounterArr, campaignArr) ?? '';

    const sourceLight = encounterField<string>(encounterArr, 'encounter_source_light');
    const { inCombatHiddenInput, rightClusterHtml } = renderSummaryCombatStatus(encounterArr, campaignArr);
    const sunState = _ftCamField(campaignArr, 'campaign_sun_state');
    const monthSeason = _ftCamField(campaignArr, 'month_season');

    // Hidden fields for JS logic
    const currentTurn = encounterField<string>(encounterArr, 'current_encounter_creature_id_turn') ?? '';

    return `
<div data-ft-encounter-summary="${encounterField(encounterArr, 'encounter_id') ?? ''}" class="p-2 mb-1 border border-2 rounded bg-gradient">
    <div class="fw-bold text-center">
        <div class="border-bottom border-dark pb-1">
            <span data-ft-summary-text="name">${encounterName}</span>
            <span class="bg-dark border p-1 rounded" data-ft-encounter-room-count="${roomCount}">
                ${iconMap('room', 15, 15, 'Number of Rooms')}
                <span data-ft-summary-text="rooms">${roomCount}</span>
            </span>
        </div>
        <div data-ft-summary-text="location">${locationParentName} ${zoneHtml}</div>
    </div>
    ${inCombatHiddenInput}
    <div class="d-flex flex-wrap justify-content-center align-items-center gap-2 mb-1">
        <div class="d-flex flex-wrap justify-content-center align-items-center">
            ${summaryIconSlot('environment', environment)}
            ${summaryIconSlot('season', monthSeason || '', true)}
            ${summaryIconSlot('weather', weather)}
            ${summaryIconSlot('sun', sunState || '', true)}
            ${summaryIconSlot('light', light)}
            ${summaryIconSlot('source', sourceLight || '', true)}
        </div>
        <div data-ft-summary-status="${[String(encounterField(encounterArr, 'in_combat') ?? '0'), _ftCamField(campaignArr, 'campaign_id') ?? '', String(encounterField(encounterArr, 'in_combat') ?? '0') === '1' ? '' : _ftCamField(campaignArr, 'campaign_disable_player_haven_return') ?? '0'].join(':')}">${rightClusterHtml}</div>
    </div>
</div>
<input type="hidden" id="input_hidden_current_encounter_creature_id_turn" value="${currentTurn}">
<input type="hidden" id="input_hidden_cleared_turn_loaded_targets" value="0">`;
}


/**
 * Campaign-time TRANSPORT indicator (player-facing). Fills the space to the LEFT of the calendar
 * card with a small icon that communicates whether campaign time is running / paused / in-combat /
 * fast-forwarding — the same states the GM drives on my_campaign.php, mirrored to players over the
 * websocket (campaign_transport_update → handleCampaignTransportUpdate stashes the flags on
 * window.FateTide.campaignTransport). When time is running the icon is the same time_moving.gif as
 * the GM toolbar and is clickable: it asks the GM to pause (requestCampaignTimePause).
 *
 * in_combat is read from the cached encounter (it is authoritative and already broadcast), so it
 * takes precedence over the GM's transient running flag.
 */
function _ftReadCampaignInCombat(): boolean {
    try {
        const ft = window.FateTide;
        const eid = String(window.ftGetActiveEncounterId?.() || '').trim();
        let encArr: unknown = ft?.encounterArr;
        if (eid && ft?.encounterCache && typeof ft.encounterCache.get === 'function') {
            const cached = ft.encounterCache.get(eid);
            if (cached) encArr = cached;
        }
        if (!encArr) return false;
        return parseInt(String(encounterField(encArr as EncounterArr, 'in_combat') ?? '0'), 10) === 1;
    } catch (_) {
        return false;
    }
}

function _ftReadCampaignTransport(campaignId: string): { running: number; fast_forward: number } {
    const ft = window.FateTide;
    const t = ft?.campaignTransport?.[campaignId];
    return {
        running: t && t.running ? 1 : 0,
        fast_forward: t && t.fast_forward ? 1 : 0,
    };
}

// Downtime mode is a persistent campaign toggle (campaign_downtime_enabled), not part of the
// transient transport broadcast — so read it from the cached campaign arr (kept current by the WS
// campaign deltas), mirroring _ftReadCampaignInCombat. Used to distinguish a plain pause from a
// downtime pause in the transport indicator.
function _ftReadCampaignDowntimeEnabled(campaignId: string): boolean {
    try {
        const ft = window.FateTide;
        let campArr: unknown = ft?.campaignArr;
        if (campaignId && ft?.campaignCache && typeof ft.campaignCache.get === 'function') {
            const cached = ft.campaignCache.get(campaignId);
            if (cached) campArr = cached;
        }
        if (!campArr) return false;
        return parseInt(_ftCamField(campArr as CampaignArr, 'campaign_downtime_enabled') ?? '0', 10) === 1;
    } catch (_) {
        return false;
    }
}

function campaignTransportKey(campaignId: string): string {
    const transport = _ftReadCampaignTransport(campaignId);
    return [!!window.FateTide?.user?.isGM, _ftReadCampaignInCombat(), transport.running, transport.fast_forward, _ftReadCampaignDowntimeEnabled(campaignId)].join(':');
}

function buildCampaignTransportIndicatorInner(campaignId: string): string {
    // Player-facing only. The GM drives the clock with the real toolbar on my_campaign.php and never
    // receives its own transport broadcast (campaignTransport stays empty for the GM), so showing a
    // mirror here would just be a confusing redundant icon. Suppress it for the GM viewer.
    if (window.FateTide?.user?.isGM) {
        return '';
    }

    // In combat → time is paused for everyone; show the in-combat marker (authoritative).
    if (_ftReadCampaignInCombat()) {
        return iconMap('in_combat_time_control', 24, 24);
    }

    const { running, fast_forward } = _ftReadCampaignTransport(campaignId);

    // Actively fast-forwarding (a GM skip is in flight) — show the fast-forward marker INSTEAD of the
    // running gif, so the display stays a single icon. fast_forward means "skipping now", not the
    // persistent toggle, so this only appears while the clock is actually jumping. The dark pill
    // distinguishes "moving faster" from plain play; still clickable to ask the GM to pause.
    if (fast_forward) {
        return `
<span
    role="button"
    class="badge bg-dark d-inline-flex align-items-center p-1"
    onclick="if (window.requestCampaignTimePause) { window.requestCampaignTimePause('${campaignId}', this); }"
>${iconMap('fast_forward', 22, 22, 'Campaign time is fast-forwarding — click to ask the GM to pause')}</span>`;
    }

    if (running) {
        return `
<span
    role="button"
    title="Campaign time is running — click to ask the GM to pause"
    class="d-inline-flex align-items-center"
    onclick="if (window.requestCampaignTimePause) { window.requestCampaignTimePause('${campaignId}', this); }"
>
    <img src="${BASE_URL_CDN}images/loaders/time_moving.gif" width="28" height="28" alt="Campaign time running">
</span>`;
    }

    // Downtime is its own campaign mode, not a paused-clock label. Let iconMap own the single tooltip.
    if (_ftReadCampaignDowntimeEnabled(campaignId)) {
        return iconMap('campaign_downtime_enabled', 24, 24);
    }
    return iconMap('pause', 24, 24, 'Campaign time is paused');
}

/**
 * Repaints ONLY the transport indicator slot in place (no calendar re-render, no DB re-fetch).
 * Called by the websocket handler (campaign-transport-update.ts) when the GM's transport state
 * changes; the calendar value-render paths already paint the indicator inline via
 * buildCampaignTransportIndicatorInner.
 */
export function ftRenderCampaignTransportIndicator(): void {
    const el = document.getElementById('ft_campaign_transport_indicator');
    if (!el) return;
    const ft = window.FateTide;
    const campaignId = String(ft?.campaign?.id || '').trim();
    if (!campaignId) return;
    const key = campaignTransportKey(campaignId);
    if (el.dataset.ftTransportState === key) return;
    el.innerHTML = buildCampaignTransportIndicatorInner(campaignId);
    el.dataset.ftTransportState = key;
}

/**
 * Player → GM ping: "please pause campaign time." POSTs a log-only row (server attributes it to the
 * requesting user), then publishes that saved row through campaign truth. Debounced per campaign.
 */
const pendingPauseRequests = new Map<string, { campaign_id: string; encounter_id: string; operation_id: string }>();
export async function requestCampaignTimePause(campaignId: string, btnEl?: HTMLElement | null): Promise<void> {
    if (!campaignId) return;
    const ft = window.FateTide ??= {};
    const cooldown = ft._campaignPauseRequestCooldown ??= {};
    if (cooldown[campaignId]) return;
    cooldown[campaignId] = true;
    setTimeout(() => { try { cooldown[campaignId] = false; } catch (_) { /* noop */ } }, 5000);

    // Visual feedback: dim + disable the clicked gif during the cooldown.
    if (btnEl) {
        try {
            btnEl.style.opacity = '0.5';
            btnEl.style.pointerEvents = 'none';
        } catch (_) { /* noop */ }
        setTimeout(() => {
            try {
                btnEl.style.opacity = '';
                btnEl.style.pointerEvents = '';
            } catch (_) { /* noop */ }
        }, 5000);
    }

    const encounter_id = String(window.ftGetActiveEncounterId?.() || '').trim();
    const command = pendingPauseRequests.get(campaignId) ?? { campaign_id: campaignId, encounter_id, operation_id: crypto.randomUUID().toUpperCase() };
    pendingPauseRequests.set(campaignId, command);
    for (let attempt = 0; attempt < 2; attempt++) {
        try {
            const { response, result } = await withAsyncDeadline(async signal => {
                const response = await fetch(`${BASE_URL}options/tools/campaigns/request_campaign_time_pause_inc.php`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(command), signal,
                });
                return { response, result: await response.json() };
            }, 20000);
            if (response.status >= 500) continue;
            if (result.ok) window.ftPushCampaignTruth?.({ type: 'janus_truth_presentation', campaign_id: campaignId,
                operation_id: command.operation_id, presentation: result.presentation });
            pendingPauseRequests.delete(campaignId);
            return;
        } catch { /* retain uncertain operation bytes for this retry or the next explicit request */ }
    }
}

/**
 * Renders the campaign time widget (a transport indicator to the left, then the date + time row and
 * the full named-date row with a link to the campaign's calendar item).
 *
 * Reads the precomputed `calendar_current_*_f` fields built in
 * get_campaign_time_format_fs() (encounter_creature_and_calendar.php).
 */
export function renderCampaignTimeWidget(campaignArr: CampaignArr | null | undefined): string {
    // calendar_current_*_f are computed values (computed.campaign); calendar_id is a campaigns column.
    const date       = _ftCamComputed(campaignArr, 'calendar_current_date_f');
    const time       = _ftCamComputed(campaignArr, 'calendar_current_time_f');
    const fullDate   = _ftCamComputed(campaignArr, 'calendar_current_full_date_named_f');
    const calendarId = _ftCamField(campaignArr, 'calendar_id');
    const campaignId = _ftCamField(campaignArr, 'campaign_id') ?? '';
    if (!campaignArr || !date || !time || !fullDate || !calendarId) {
        return '<div class="text-danger">Missing campaign time data</div>';
    }

    const calendarLink = `${BASE_URL}item.php?id=${calendarId}`;

    // Transport indicator slot (player-facing; empty string for the GM viewer → slot omitted so the
    // calendar stays centered with no left gap).
    const indicatorInner = buildCampaignTransportIndicatorInner(campaignId);
    const indicatorSlot = indicatorInner
        ? `<div id="ft_campaign_transport_indicator" data-ft-transport-state="${campaignTransportKey(campaignId)}" class="ft-campaign-transport-indicator d-flex align-items-center justify-content-center flex-shrink-0">${indicatorInner}</div>`
        : '';

    return `
<div data-ft-campaign-time-root class="cl_refresh_campaign_time d-flex align-items-center justify-content-center gap-2" style="min-width: 225px; min-height:50px;">
    ${indicatorSlot}
    <div class="flex-shrink-0">
        <div class="d-flex justify-content-center align-items-center mb-1">
            <div class="p-1 mr-1 border rounded">${iconMap('calendar_date', 25, 25, true)} <span data-ft-campaign-time-value="date">${date}</span></div>
            <div class="p-1 border rounded">${iconMap('calendar_time', 25, 25, true)} <span data-ft-campaign-time-value="time">${time}</span></div>
        </div>

        <div class="d-flex justify-content-center align-items-center">
            <div class="p-1 border rounded">
                <a data-ft-campaign-time-calendar-link target="_blank" href="${calendarLink}">
                    ${iconMap('calendar', 25, 25, true)}
                </a> <span data-ft-campaign-time-value="full-date">${fullDate}</span>
            </div>
        </div>
    </div>
</div>`;
}

/** Existing context inputs and timer nodes survive ordinary encounter/campaign pushes. */
export function patchEncounterSummary(
    changedFields: readonly string[] | null = null,
    encounterArr?: EncounterArr | null,
    campaignArr?: CampaignArr | null,
): boolean {
    const root = document.getElementById('div_refresh_encounter_state');
    const summary = root?.querySelector<HTMLElement>('[data-ft-encounter-summary]');
    const encounter = (encounterArr ?? window.FateTide?.encounterArr) as EncounterArr | null | undefined;
    const campaign = (campaignArr ?? window.FateTide?.campaignArr ?? {}) as CampaignArr;
    if (!root || !summary || !encounter
        || summary.dataset.ftEncounterSummary !== String(encounterField(encounter, 'encounter_id') ?? '')) return false;
    const due = (...fields: string[]): boolean => !changedFields || fields.some(field => changedFields.includes(field));
    const text = (selector: string, value: unknown): void => {
        const leaf = root.querySelector(selector);
        const next = decodeHtmlEntitiesForPlainText(String(value ?? ''));
        if (leaf && leaf.textContent !== next) leaf.textContent = next;
    };
    if (due('encounter_name')) text('[data-ft-summary-text="name"]', encounterField(encounter, 'encounter_name') ?? 'Unknown Encounter');
    if (due('encounter_shape')) {
        const count = encounterRoomCount(encounter);
        text('[data-ft-summary-text="rooms"]', count);
        const badge = root.querySelector<HTMLElement>('[data-ft-encounter-room-count]');
        if (badge && badge.dataset.ftEncounterRoomCount !== String(count)) badge.dataset.ftEncounterRoomCount = String(count);
    }
    if (due('encounter_location_parent_name', 'encounter_zone', 'permissions')) {
        const zone = computedValue<string>(encounter, 'encounter', 'encounter_zone');
        text('[data-ft-summary-text="location"]', `${encounterField(encounter, 'encounter_location_parent_name') ?? ''} ${zone && hasCampaignPermission(campaign) ? `(${zone})` : ''}`);
    }
    const icons: [string, string, boolean, string[]][] = [
        ['environment', String(encounterField(encounter, 'encounter_environment') ?? ''), false, ['encounter_environment']],
        ['weather', String(encounterField(encounter, 'encounter_weather') ?? ''), false, ['encounter_weather']],
        ['light', effectiveEncounterLightId(encounter, campaign) ?? '', false, ['encounter_light', 'campaign_light', 'campaign_light_cycle_mode', 'campaign_sun_state']],
        ['source', String(encounterField(encounter, 'encounter_source_light') ?? ''), true, ['encounter_source_light']],
        ['sun', _ftCamField(campaign, 'campaign_sun_state') ?? '', true, ['campaign_sun_state']],
        ['season', _ftCamField(campaign, 'month_season') ?? '', true, ['month_season']],
    ];
    for (const [key, value, optional, fields] of icons) {
        if (!due(...fields)) continue;
        const leaf = root.querySelector<HTMLElement>(`[data-ft-summary-icon="${key}"]`);
        if (!leaf || leaf.dataset.ftSummaryValue === value) continue;
        leaf.dataset.ftSummaryValue = value;
        leaf.hidden = optional && !value;
        leaf.innerHTML = `<span role="button" onclick="openItemInfo('${value}');">${optional && !value ? '' : iconMap(value)}</span>`;
    }
    const combat = String(encounterField(encounter, 'in_combat') ?? '0');
    const input = root.querySelector<HTMLInputElement>('#input_hidden_encounter_quick_summary_in_combat');
    const status = root.querySelector<HTMLElement>('[data-ft-summary-status]');
    const haven = _ftCamField(campaign, 'campaign_disable_player_haven_return') ?? '0';
    const statusKey = [combat, _ftCamField(campaign, 'campaign_id') ?? '', combat === '1' ? '' : haven].join(':');
    if (input && status && (input.value !== combat || (status.dataset.ftSummaryStatus !== undefined && status.dataset.ftSummaryStatus !== statusKey))) {
        input.value = combat;
        status.innerHTML = renderSummaryCombatStatus(encounter, campaign).rightClusterHtml;
    }
    if (status && status.dataset.ftSummaryStatus !== statusKey) status.dataset.ftSummaryStatus = statusKey;
    if (due('current_round')) {
        const round = encounterField(encounter, 'current_round') ?? '';
        text('#span_encounter_quick_summary_round', round);
        const badge = root.querySelector<HTMLElement>('#span_encounter_quick_summary_round');
        if (badge && badge.hidden !== (round === '')) badge.hidden = round === '';
        if (badge && badge.classList.contains('d-none') !== (round === '')) badge.classList.toggle('d-none', round === '');
    }
    const turn = root.querySelector<HTMLInputElement>('#input_hidden_current_encounter_creature_id_turn');
    const turnId = String(encounterField(encounter, 'current_encounter_creature_id_turn') ?? '');
    if (turn && turn.value !== turnId) turn.value = turnId;
    const calendarSlot = root.querySelector<HTMLElement>('[data-ft-summary-calendar]');
    const validTime = ['calendar_current_date_f', 'calendar_current_time_f', 'calendar_current_full_date_named_f']
        .every(field => !!_ftCamComputed(campaign, field)) && !!_ftCamField(campaign, 'calendar_id');
    const clock = root.querySelector('[data-ft-campaign-time-root]');
    if (calendarSlot && validTime !== !!clock) {
        const html = validTime ? renderCampaignTimeWidget(campaign)
            : (_ftCamField(campaign, 'campaign_id') ? '<div class="text-danger">Missing campaign time data</div>'
                : '<div class="text-muted small text-center my-2">No campaign time.</div>');
        if (calendarSlot.innerHTML !== html) calendarSlot.innerHTML = html;
    }
    const values: readonly [string, string][] = [
        ['date', 'calendar_current_date_f'], ['time', 'calendar_current_time_f'],
        ['full-date', 'calendar_current_full_date_named_f'],
    ];
    for (const [key, field] of values) if (due(field)) text(`[data-ft-campaign-time-value="${key}"]`, _ftCamComputed(campaign, field));
    const link = root.querySelector('[data-ft-campaign-time-calendar-link]');
    const calendarId = _ftCamField(campaign, 'calendar_id');
    if (link && calendarId && link.getAttribute('href') !== `${BASE_URL}item.php?id=${calendarId}`) link.setAttribute('href', `${BASE_URL}item.php?id=${calendarId}`);
    ftRenderCampaignTransportIndicator();
    return true;
}
